// Kézbesítés (2.3/2.4, UI-7): how the sending landed, pool by pool and
// provider by provider, measured against rule 4 — and what the receiving
// side says about us. During a warmup this is the screen that has to answer,
// fast: is anything red, how did yesterday and today land, and how close is
// each pool to a line.
//
// The days are UTC days, because the budget and the rule count on that
// boundary; the range picker says where a day starts on each country's clock.
// Today is counted live from the source tables (the morning aggregate only
// holds its first six hours), and every day says when its figures were made.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { FileTextIcon, RefreshCwIcon } from "lucide-react";
import {
  ApiError,
  acknowledgeAlert,
  fetchAlerts,
  fetchDigestHtml,
  fetchReputation,
  fetchSeedChecks,
} from "@/lib/api";
import type { AlertRow, ReputationRow, SeedCheck } from "@/lib/api";
import {
  fetchDailyLive,
  fetchDmarc,
  fetchDnsbl,
  fetchEvidence,
  fetchPoolMeta,
  fetchTypoDomains,
  fetchWatchSources,
} from "@/lib/tracking";
import type {
  DeliveryEvidence,
  DmarcDomain,
  DnsblIp,
  EvidenceClass,
  EvidenceIpIsp,
  LivePoolDay,
  TypoDomainRow,
  TypoDomains,
  WatchSource,
} from "@/lib/tracking";
import { useAuth } from "@/lib/auth";
import { useLoad } from "@/lib/useLoad";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { NativeSelect } from "@/components/ui/native-select";
import { DataTable } from "@/components/emc/DataTable";
import { DayRangePicker } from "@/components/emc/DayRangePicker";
import { EnumText } from "@/components/emc/EnumText";
import { PageHeader } from "@/components/emc/PageHeader";
import { PoolLink } from "@/components/emc/PoolLink";
import { RuleRate } from "@/components/emc/RuleRate";
import { StatusPill } from "@/components/emc/StatusPill";
import { Toolbar } from "@/components/emc/Toolbar";
import { Callout, EmptyState, ErrorState, LoadingState } from "@/components/emc/states";
import { addDays, presetRange, utcDay } from "@/components/emc/days";
import type { DayRange } from "@/components/emc/days";
import { count, minute } from "@/components/emc/format";
import { labelOf, statusLook } from "@/components/emc/status";
import { POOL_MIN_SAMPLE, percentText, rateText } from "@/components/emc/thresholds";
import {
  classLabel,
  classSplit,
  dayRows,
  fetchWindow,
  rangeTable,
  redNow,
  senderLabel,
} from "@/pages/deliverability/model";
import type { DayRow, RangeRow, RedItem } from "@/pages/deliverability/model";
import { cn } from "@/lib/utils";

// Often enough to see a warmup day move, rarely enough that the live recount
// is not what the database spends its afternoon on.
const REFRESH_MS = 120_000;
// The strip's first screenful; the rest is one click away.
const STRIP_SHOWN = 8;

function messageOf(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) return error.message;
  return "Ismeretlen hiba.";
}

function fraction(value: unknown): string {
  return typeof value === "number" ? percentText(value * 100) : "—";
}

function shareText(part: number, whole: number): string {
  return rateText(part, whole);
}

// --- Mi piros most -----------------------------------------------------------------

function RedStrip({
  items,
  loading,
  asOf,
  todaySent,
  yesterdaySent,
}: {
  items: RedItem[];
  loading: boolean;
  asOf: Date | null;
  todaySent: number;
  yesterdaySent: number;
}) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, STRIP_SHOWN);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Mi piros most</CardTitle>
        <CardDescription>
          Ma eddig és tegnap, poolonként és levelezőnként a 4. szabály két határához mérve
          (emelés csak 2% bounce és 0,1% panasz alatt; megállítás 3% bounce vagy 0,2% panasz
          fölött, {POOL_MIN_SAMPLE} küldéstől) — és a megállított vagy lassított poolok, a
          feketelisták, a Postmaster és az SNDS ítélete, a nyugtázatlan kritikus riasztások.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {loading ? (
          <LoadingState lines={2} />
        ) : items.length === 0 ? (
          <Callout tone="success">
            Most semmi sincs a küszöbök fölött vagy közelében. Ma eddig {count(todaySent)}, tegnap{" "}
            {count(yesterdaySent)} levél ment ki.
          </Callout>
        ) : (
          <ul className="m-0 flex list-none flex-col p-0">
            {shown.map((item) => (
              <li
                key={item.key}
                className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-line py-2 last:border-b-0"
              >
                <StatusPill tone={item.tone} className="self-center">
                  {item.label}
                </StatusPill>
                <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2">
                  {item.pool && <PoolLink pool={item.pool} />}
                  {item.subject && (
                    <span className={cn(/^\d/.test(item.subject) && "data-mono")}>{item.subject}</span>
                  )}
                  {item.when && <span className="text-muted-foreground">{item.when}</span>}
                  <span className="min-w-0">— {item.text}</span>
                </span>
                {item.action &&
                  (item.action.to.startsWith("#") ? (
                    <a href={item.action.to} className="text-label">
                      {item.action.label}
                    </a>
                  ) : (
                    <Link to={item.action.to} className="text-label">
                      {item.action.label}
                    </Link>
                  ))}
              </li>
            ))}
          </ul>
        )}
        {items.length > STRIP_SHOWN && (
          <Button variant="link" type="button" className="w-fit text-label" onClick={() => setAll(!all)}>
            {all ? "Csak a legsúlyosabbak" : `Mind a ${items.length} tétel`}
          </Button>
        )}
        {asOf && (
          <p className="text-label text-muted-foreground">
            A mai számok élő számolásból, {minute(asOf.toISOString())}-kor; a percenkénti
            automatikus megállítás ugyanezekre a határokra néz.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// --- the range table ------------------------------------------------------------------

function rangeColumns(countriesOf: (pool: string) => string): ColumnDef<RangeRow>[] {
  return [
    {
      id: "who",
      header: "Pool / levelező",
      cell: ({ row }) =>
        row.original.isp === null ? (
          <span className="flex items-baseline gap-2">
            <PoolLink pool={row.original.pool} />
            <span className="data-mono text-label text-muted-foreground">{countriesOf(row.original.pool)}</span>
          </span>
        ) : (
          <span className="pl-5">
            <EnumText domain="isp" value={row.original.isp} />
          </span>
        ),
    },
    {
      id: "sent",
      header: "Küldve",
      cell: ({ row }) => count(row.original.figures.sent),
      meta: { align: "right" },
    },
    {
      id: "delivered",
      header: "Kézbesítve",
      cell: ({ row }) => (
        <span title={`${count(row.original.figures.delivered)} kézbesítés`}>
          {shareText(row.original.figures.delivered, row.original.figures.sent)}
        </span>
      ),
      meta: { align: "right" },
    },
    {
      id: "bounce",
      header: "Bounce · 8 nap",
      cell: ({ row }) => (
        <RuleRate
          verdict={row.original.bounce}
          minSample={POOL_MIN_SAMPLE}
          provider={row.original.isp !== null}
          note={row.original.bounce.note}
          trend={row.original.bounceSeries.values}
          trendTone={row.original.bounceSeries.tone}
        />
      ),
      meta: { align: "right" },
    },
    {
      id: "complaint",
      header: "Panasz · 8 nap",
      cell: ({ row }) => (
        <RuleRate
          verdict={row.original.complaint}
          minSample={POOL_MIN_SAMPLE}
          provider={row.original.isp !== null}
          note={row.original.complaint.note}
          trend={row.original.complaintSeries.values}
          trendTone={row.original.complaintSeries.tone}
        />
      ),
      meta: { align: "right" },
    },
    {
      id: "delayed",
      header: "Halasztás",
      cell: ({ row }) => (
        <span title={`${count(row.original.figures.delayed)} késleltetés`}>
          {shareText(row.original.figures.delayed, row.original.figures.sent)}
        </span>
      ),
      meta: { align: "right", muted: true },
    },
    {
      id: "unsubscribed",
      header: "Leiratkozás",
      cell: ({ row }) => (
        <span title={`${count(row.original.figures.unsubscribed)} leiratkozás`}>
          {shareText(row.original.figures.unsubscribed, row.original.figures.sent)}
        </span>
      ),
      meta: { align: "right", muted: true },
    },
  ];
}

// --- day by day ---------------------------------------------------------------------

function made(row: DayRow): string {
  if (row.live) return `élő · ${minute(row.recomputed_at)}`;
  return row.recomputed_at ? `újraszámolva ${minute(row.recomputed_at)}` : "—";
}

const DAY_COLUMNS: ColumnDef<DayRow>[] = [
  { id: "day", header: "Nap", cell: ({ row }) => row.original.day, meta: { mono: true } },
  { id: "pool", header: "Pool", cell: ({ row }) => <PoolLink pool={row.original.pool} /> },
  { id: "sent", header: "Küldve", cell: ({ row }) => count(row.original.figures.sent), meta: { align: "right" } },
  {
    id: "delivered",
    header: "Kézbesítve",
    cell: ({ row }) => shareText(row.original.figures.delivered, row.original.figures.sent),
    meta: { align: "right" },
  },
  {
    id: "bounce",
    header: "Bounce",
    cell: ({ row }) => <RuleRate verdict={row.original.bounce} minSample={POOL_MIN_SAMPLE} />,
    meta: { align: "right" },
  },
  {
    id: "complaint",
    header: "Panasz",
    cell: ({ row }) => <RuleRate verdict={row.original.complaint} minSample={POOL_MIN_SAMPLE} />,
    meta: { align: "right" },
  },
  {
    id: "delayed",
    header: "Halasztás",
    cell: ({ row }) => shareText(row.original.figures.delayed, row.original.figures.sent),
    meta: { align: "right", muted: true },
  },
  {
    id: "made",
    header: "Mikori",
    cell: ({ row }) => (
      <span className="inline-flex items-center gap-2">
        <span className="text-muted-foreground">{made(row.original)}</span>
        {row.original.disagreement.length > 0 && (
          <StatusPill
            tone="warn"
            title={row.original.disagreement
              .map(
                (item) =>
                  `${item.field}: a számláló ${count(item.counted)}, az újraszámolás ${count(item.recomputed)}`,
              )
              .join(" · ")}
          >
            eltérés
          </StatusPill>
        )}
      </span>
    ),
  },
];

const ISP_DAY_COLUMNS: ColumnDef<LivePoolDay["by_isp"][number]>[] = [
  { id: "isp", header: "Levelező", cell: ({ row }) => <EnumText domain="isp" value={row.original.isp} /> },
  { id: "sent", header: "Küldve", cell: ({ row }) => count(row.original.sent), meta: { align: "right" } },
  {
    id: "delivered",
    header: "Kézbesítve",
    cell: ({ row }) => count(row.original.delivered),
    meta: { align: "right" },
  },
  { id: "bounced", header: "Bounce", cell: ({ row }) => count(row.original.bounced), meta: { align: "right" } },
  {
    id: "complained",
    header: "Panasz",
    cell: ({ row }) => count(row.original.complained),
    meta: { align: "right" },
  },
  { id: "delayed", header: "Halasztás", cell: ({ row }) => count(row.original.delayed), meta: { align: "right" } },
  { id: "rejected", header: "Elutasítva", cell: ({ row }) => count(row.original.rejected), meta: { align: "right" } },
  {
    id: "unsubscribed",
    header: "Leiratkozás",
    cell: ({ row }) => count(row.original.unsubscribed),
    meta: { align: "right" },
  },
];

// --- the evidence: provider × sending IP (slice 5.4) -------------------------------

const EVIDENCE_COLUMNS: ColumnDef<EvidenceIpIsp>[] = [
  {
    id: "sending_ip",
    header: "Küldő IP",
    cell: ({ row }) => (
      <span className={cn(row.original.sending_ip ? "data-mono" : "text-muted-foreground")}>
        {senderLabel(row.original.sending_ip)}
      </span>
    ),
  },
  { id: "isp", header: "Levelező", cell: ({ row }) => <EnumText domain="isp" value={row.original.isp} /> },
  {
    id: "sent",
    header: "Kimenettel",
    cell: ({ row }) => (
      <span className="flex flex-col items-end">
        <span>{count(row.original.sent)}</span>
        {row.original.sent_from_tag > 0 && (
          <span className="text-label text-muted-foreground">
            SES-címke szerint {count(row.original.sent_from_tag)}
          </span>
        )}
      </span>
    ),
    meta: { align: "right" },
  },
  {
    id: "delivered",
    header: "Kézbesítve",
    cell: ({ row }) => count(row.original.delivered),
    meta: { align: "right" },
  },
  {
    id: "deferred",
    header: "Halasztás",
    cell: ({ row }) => (
      <span className="flex flex-col items-end">
        <span>{count(row.original.deferred)}</span>
        {row.original.deferred > 0 && (
          <span className="text-label text-muted-foreground">{classSplit(row.original.deferred_by_class)}</span>
        )}
      </span>
    ),
    meta: { align: "right" },
  },
  {
    id: "bounced",
    header: "Bounce",
    cell: ({ row }) => (
      <span className="flex flex-col items-end">
        <span>{count(row.original.bounced)}</span>
        {row.original.bounced > 0 && (
          <span className="text-label text-muted-foreground">{classSplit(row.original.bounced_by_class)}</span>
        )}
      </span>
    ),
    meta: { align: "right" },
  },
  {
    id: "complained",
    header: "Panasz",
    cell: ({ row }) => count(row.original.complained),
    meta: { align: "right" },
  },
];

const CLASS_COLUMNS: ColumnDef<EvidenceClass>[] = [
  { id: "class", header: "Ok", cell: ({ row }) => classLabel(row.original.diagnostic_class) },
  { id: "bounced", header: "Bounce", cell: ({ row }) => count(row.original.bounced), meta: { align: "right" } },
  {
    id: "deferred",
    header: "Halasztás",
    cell: ({ row }) => count(row.original.deferred),
    meta: { align: "right" },
  },
];

function EvidenceCard({ data, error }: { data: DeliveryEvidence | null; error: string | null }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Levelezők és küldő IP-k</CardTitle>
        <CardDescription>
          Az időszak összesen, minden visszajelzésből: melyik IP-ről melyik levelezőhöz ment, és ami nem
          ment át, miért (a szerver válaszából besorolva). Csak gyűjtés — ebből még semmi nem dönt. A
          „kimenettel” a kézbesített és a visszapattant levél: hogy egy levél melyik IP-ről ment, azt az
          SES csak a válasszal együtt mondja meg.
        </CardDescription>
        {data !== null && data.live_days.length > 0 && (
          <CardAction>
            <span className="text-label text-muted-foreground">élő: {data.live_days.join(", ")}</span>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {error !== null && <ErrorState message={error} />}
        {data === null ? (
          error === null && <LoadingState lines={2} />
        ) : data.by_ip_isp.length === 0 ? (
          <EmptyState bare title="Ebben az időszakban nem jött visszajelzés." />
        ) : (
          <>
            <DataTable
              label="Levelezők és küldő IP-k"
              variant="plain"
              className="-mx-5.5 border-t border-separator"
              columns={EVIDENCE_COLUMNS}
              data={data.by_ip_isp}
              getRowId={(row) => `${row.sending_ip}|${row.isp}`}
            />
            {data.by_class.length > 0 && (
              <DataTable
                label="Okok"
                variant="plain"
                className="rounded-card border border-line bg-surface"
                columns={CLASS_COLUMNS}
                data={data.by_class}
                getRowId={(row) => row.diagnostic_class}
              />
            )}
            {data.recomputed_at && (
              <p className="text-label text-muted-foreground">
                A tárolt órák utolsó újraszámolása: {minute(data.recomputed_at)}.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// --- what the receiving side says -------------------------------------------------

function latestBySubject(rows: ReputationRow[], source: string): ReputationRow[] {
  const latest = new Map<string, ReputationRow>();
  for (const row of rows) {
    if (row.source !== source) continue;
    const seen = latest.get(row.subject);
    if (seen === undefined || seen.day < row.day) latest.set(row.subject, row);
  }
  return [...latest.values()].sort((left, right) => left.subject.localeCompare(right.subject));
}

/** "Nincs adat", and why — never a zero standing in for a source that did not speak. */
function NoData({ source, sources, what }: { source: string; sources: WatchSource[] | null; what: string }) {
  const run = sources?.find((item) => item.source === source);
  if (sources === null) return <p className="text-muted-foreground">nincs adat</p>;
  if (run === undefined) {
    return (
      <p className="text-muted-foreground">
        <span className="font-medium text-ink">nincs adat</span> — a(z) {labelOf("watchSource", source)} nincs
        bekötve, egyszer sem futott. {what}
      </p>
    );
  }
  return (
    <p className="text-muted-foreground">
      <span className="font-medium text-ink">nincs adat erre az időszakra</span> — utoljára{" "}
      {minute(run.last_run_at)}-kor futott ({statusLook("sourceRun", run.last_status).label}).
    </p>
  );
}

function ipReputations(value: unknown): string {
  if (!Array.isArray(value) || value.length === 0) return "";
  return value
    .map((item: unknown) => {
      if (item === null || typeof item !== "object") return null;
      const entry = item as { reputation?: unknown; ipCount?: unknown };
      const word =
        typeof entry.reputation === "string" ? statusLook("postmasterReputation", entry.reputation).label : "?";
      return entry.ipCount !== undefined ? `${String(entry.ipCount)} IP ${word}` : word;
    })
    .filter((part): part is string => part !== null)
    .join(", ");
}

/** The subject, and under it the day the source said it about. */
function SubjectDay({ row }: { row: ReputationRow }) {
  return (
    <span className="flex flex-col">
      <span className="data-mono">{row.subject}</span>
      <span className="data-mono text-label text-muted-foreground">{row.day}</span>
    </span>
  );
}

const POSTMASTER_COLUMNS: ColumnDef<ReputationRow>[] = [
  { id: "subject", header: "Tartomány · nap", cell: ({ row }) => <SubjectDay row={row.original} /> },
  {
    id: "domain",
    header: "Reputáció",
    cell: ({ row }) => {
      const value = row.original.metrics.domain_reputation;
      const ips = ipReputations(row.original.metrics.ip_reputations);
      return typeof value === "string" ? (
        <StatusPill
          domain="postmasterReputation"
          state={value}
          title={ips ? `${value} — az IP-k: ${ips}` : value}
        />
      ) : (
        <span className="text-muted-foreground">nincs adat</span>
      );
    },
  },
  {
    id: "spam",
    header: "Spam-arány",
    cell: ({ row }) => fraction(row.original.metrics.spam_rate),
    meta: { align: "right" },
  },
  {
    id: "auth",
    header: "DMARC",
    // DMARC passes on either SPF or DKIM, so its success rate is the one that
    // says whether Gmail took the mail for ours; the other two are on hover.
    cell: ({ row }) => (
      <span
        title={`SPF ${fraction(row.original.metrics.spf_success)} · DKIM ${fraction(row.original.metrics.dkim_success)}`}
      >
        {fraction(row.original.metrics.dmarc_success)}
      </span>
    ),
    meta: { align: "right", muted: true },
  },
];

function wholeOrDash(value: unknown): string {
  return typeof value === "number" ? count(value) : "—";
}

const SNDS_COLUMNS: ColumnDef<ReputationRow>[] = [
  { id: "subject", header: "IP · nap", cell: ({ row }) => <SubjectDay row={row.original} /> },
  {
    id: "filter",
    header: "Szűrés",
    cell: ({ row }) => {
      const value = row.original.metrics.filter_result;
      return typeof value === "string" ? (
        <StatusPill domain="sndsFilter" state={value} />
      ) : (
        <span className="text-muted-foreground">nincs adat</span>
      );
    },
  },
  {
    id: "complaint",
    header: "Panaszarány",
    cell: ({ row }) => fraction(row.original.metrics.complaint_rate),
    meta: { align: "right" },
  },
  {
    id: "traps",
    header: "Csapda",
    cell: ({ row }) => wholeOrDash(row.original.metrics.trap_hits),
    meta: { align: "right" },
  },
];

function ReputationCard({
  reputation,
  sources,
  error,
}: {
  reputation: ReputationRow[] | null;
  sources: WatchSource[] | null;
  error: string | null;
}) {
  const postmaster = latestBySubject(reputation ?? [], "postmaster");
  const snds = latestBySubject(reputation ?? [], "snds");
  return (
    <Card>
      <CardHeader>
        <CardTitle>Külső vélemény</CardTitle>
        <CardDescription>
          Amit a Gmail (Postmaster) és a Microsoft (SNDS) mond rólunk — egy levél, amit a spam
          mappába tesznek, a SES szerint kézbesített. Tartományonként és IP-nként a legutóbbi nap
          az utolsó két hétből; a Postmaster 1–2 napot késik.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {error !== null && <ErrorState message={error} />}
        <div className="grid gap-6 lg:grid-cols-2">
          <section className="flex min-w-0 flex-col gap-2">
            <h3>Google Postmaster</h3>
            {reputation === null ? (
              <LoadingState lines={1} />
            ) : postmaster.length === 0 ? (
              <NoData
                source="postmaster"
                sources={sources}
                what="Service-account vagy token kell hozzá (KERDESEK 1.6)."
              />
            ) : (
              <DataTable
                label="Google Postmaster"
                variant="plain"
                className="rounded-inner border border-separator"
                data={postmaster}
                getRowId={(row) => row.subject}
                columns={POSTMASTER_COLUMNS}
              />
            )}
          </section>
          <section className="flex min-w-0 flex-col gap-2">
            <h3>Microsoft SNDS</h3>
            {reputation === null ? (
              <LoadingState lines={1} />
            ) : snds.length === 0 ? (
              <NoData source="snds" sources={sources} what="Az SNDS-kulcs kell hozzá (EMC_SNDS_KEY, KERDESEK 1.6)." />
            ) : (
              <DataTable
                label="Microsoft SNDS"
                variant="plain"
                className="rounded-inner border border-separator"
                data={snds}
                getRowId={(row) => row.subject}
                columns={SNDS_COLUMNS}
              />
            )}
          </section>
        </div>
      </CardContent>
    </Card>
  );
}

const DNSBL_COLUMNS: ColumnDef<DnsblIp>[] = [
  { id: "ip", header: "IP", cell: ({ row }) => row.original.ip, meta: { className: "font-mono" } },
  {
    id: "level",
    header: "Állapot",
    cell: ({ row }) => <StatusPill domain="listing" state={row.original.level} />,
  },
  {
    id: "zones",
    header: "Zóna",
    cell: ({ row }) =>
      row.original.listed_on.length > 0
        ? row.original.listed_on.join(", ")
        : row.original.unverifiable.length > 0
          ? `nem kérdezhető: ${row.original.unverifiable.join(", ")}`
          : `${row.original.zones.length} zónán tiszta`,
    meta: { wrap: true },
  },
  {
    id: "checks",
    header: "Ellenőrzés",
    cell: ({ row }) => (
      <span className="flex flex-col">
        <span>
          {row.original.listed_checks > 0
            ? `${row.original.listed_checks} / ${row.original.checks} listán`
            : `${row.original.checks} tiszta`}
        </span>
        <span className="data-mono text-label text-muted-foreground">{minute(row.original.observed_at)}</span>
      </span>
    ),
    meta: { muted: true },
  },
];

function DnsblCard({
  rows,
  sources,
  error,
}: {
  rows: DnsblIp[] | null;
  sources: WatchSource[] | null;
  error: string | null;
}) {
  const [all, setAll] = useState(false);
  // A listing, a zone that could not be asked, or a listing earlier in the
  // window is news; twenty clean addresses are one sentence.
  const news = (rows ?? []).filter((row) => row.level !== "ok" || row.listed_checks > 0);
  const clean = (rows ?? []).length - news.length;
  const latest = (rows ?? []).reduce<string | null>(
    (found, row) => (found === null || row.observed_at > found ? row.observed_at : found),
    null,
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle>Feketelisták</CardTitle>
        <CardDescription>
          Minden küldő IP a DNSBL-zónákon, óránként; egy találat megállítja a poolt, és csak ember
          engedi tovább. Az utolsó két nap, címenként a legutóbbi válasz.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {error !== null && <ErrorState message={error} />}
        {rows === null ? (
          <LoadingState lines={2} />
        ) : rows.length === 0 ? (
          <NoData
            source="dnsbl"
            sources={sources}
            what="A címlista az EMC_POOL_IPS-ben van; amíg üres, nincs mit ellenőrizni."
          />
        ) : (
          <>
            {(all ? rows : news).length > 0 && (
              <DataTable
                label="Feketelisták"
                variant="plain"
                className="-mx-5.5 border-t border-separator"
                data={all ? rows : news}
                getRowId={(row) => row.ip}
                columns={DNSBL_COLUMNS}
              />
            )}
            <p className="flex flex-wrap items-baseline gap-x-2 text-muted-foreground">
              {clean > 0 && (
                <span>
                  {news.length > 0 ? "A többi " : "Mind a "}
                  {clean} cím tiszta minden zónán
                  {latest ? `, legutóbb ${minute(latest)}-kor ellenőrizve` : ""}.
                </span>
              )}
              <Button variant="link" type="button" className="text-label" onClick={() => setAll(!all)}>
                {all ? "Csak ami baj" : `Mind a ${rows.length} cím`}
              </Button>
            </p>
          </>
        )}
        <p className="text-label text-muted-foreground">
          Hogy egy cím melyik poolé, azt a worker EMC_POOL_IPS-e tudja; a megállítás és a feloldás
          az <Link to="/orszem?source=dnsbl">Őrszemen</Link> látszik.
        </p>
      </CardContent>
    </Card>
  );
}

// --- Elírt domainek (2026-10-30) ------------------------------------------------------

const TYPO_STATE: Record<string, string> = {
  unchecked: "még nem kérdezett",
  unknown: "a DNS nem válaszolt",
  receives: "fogad levelet",
  suspect: "egy válasz szerint halott",
  dead: "halott, a döntés vár",
  decided: "eldöntve",
};
// Domains whose addresses the rule has not decided yet, for want of a fact.
const TYPO_WAITING = ["unchecked", "unknown", "suspect", "dead"];

const TYPO_COLUMNS: ColumnDef<TypoDomainRow>[] = [
  {
    id: "domain",
    header: "Domain",
    cell: ({ row }) => (
      <span className="flex flex-col">
        <span className="data-mono">
          {row.original.domain}
          {row.original.target ? ` → ${row.original.target}` : ""}
        </span>
        <span className="text-label text-muted-foreground">
          {TYPO_STATE[row.original.state] ?? row.original.state}
          {row.original.why ? ` · ${row.original.why}` : ""}
          {row.original.state === "decided" && !row.original.target
            ? row.original.candidates.length > 1
              ? ` · több jelölt: ${row.original.candidates.join(", ")}`
              : " · nincs jelölt"
            : ""}
        </span>
      </span>
    ),
  },
  { id: "contacts", header: "Cím", cell: ({ row }) => count(row.original.contacts), meta: { align: "right" } },
  { id: "corrected", header: "Javítva", cell: ({ row }) => count(row.original.corrected), meta: { align: "right" } },
  { id: "twin", header: "Megvolt", cell: ({ row }) => count(row.original.twin), meta: { align: "right" } },
  { id: "dead", header: "Csak kizárva", cell: ({ row }) => count(row.original.dead), meta: { align: "right" } },
];

function TypoDomainsCard({ data, error }: { data: TypoDomains | null; error: string | null }) {
  const waiting = data
    ? TYPO_WAITING.reduce(
        (sum, state) => ({
          domains: sum.domains + (data.states[state]?.domains ?? 0),
          contacts: sum.contacts + (data.states[state]?.contacts ?? 0),
        }),
        { domains: 0, contacts: 0 },
      )
    : null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Elírt domainek</CardTitle>
        <CardDescription>
          Ami a DNS szerint nem fogad levelet (két válasz, legalább egy óra különbséggel), annak a
          címe végleges, globális kizárás. Ha egyetlen ismert szolgáltató van egy elütésnyire, és az
          országban még nincs meg a helyes cím, javított kontakt készül a hozzájárulással és a
          listákkal. A DNS-hiba nem tény: újra megkérdezzük.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {error !== null && <ErrorState message={error} />}
        {data === null ? (
          error === null && <LoadingState lines={2} />
        ) : (
          <>
            <p className="flex flex-wrap gap-x-4 gap-y-1">
              <span>
                <strong>{count(data.outcomes.corrected)}</strong> javítva
              </span>
              <span>
                <strong>{count(data.outcomes.twin)}</strong> kizárva, a helyes cím megvolt
              </span>
              <span>
                <strong>{count(data.outcomes.dead)}</strong> kizárva, javítás nélkül
              </span>
              {waiting !== null && waiting.contacts > 0 && (
                <span className="text-muted-foreground">
                  {count(waiting.contacts)} cím ({count(waiting.domains)} domain) DNS-tényre vár
                </span>
              )}
            </p>
            {data.domains.length > 0 ? (
              <DataTable
                label="Elírt domainek"
                variant="plain"
                className="-mx-5.5 border-t border-separator"
                data={data.domains}
                getRowId={(row) => row.domain}
                columns={TYPO_COLUMNS}
              />
            ) : (
              <EmptyState bare title="Még nincs halott domain a kontaktok között." />
            )}
            <p className="text-label text-muted-foreground">
              {data.last_checked_at ? `Utolsó DNS-válasz ${minute(data.last_checked_at)}-kor. ` : "A DNS-ellenőrzés még nem futott. "}
              {data.superseded > 0 &&
                `${count(data.superseded)} javított cím azóta saját Klaviyo-profilként is megjött; azt a profil írja. `}
              A javított kontakt hozzájárulása a Klaviyo-profilét követi.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

const DMARC_COLUMNS: ColumnDef<DmarcDomain>[] = [
  {
    id: "domain",
    header: "Tartomány",
    cell: ({ row }) => (
      <span className="flex flex-col">
        <span className="data-mono">{row.original.domain}</span>
        <span className="text-label text-muted-foreground">
          {row.original.reports < row.original.reports_in_window
            ? `${count(row.original.reports)} / ${count(row.original.reports_in_window)} riport olvasva, a legújabbak — a számok csak ezekről szólnak`
            : `${count(row.original.reports)} riport`}
          {row.original.reporters.length > 0 && ` · ${row.original.reporters.join(", ")}`}
        </span>
      </span>
    ),
  },
  {
    id: "messages",
    header: "Levél",
    // Nothing read is not zero mail.
    cell: ({ row }) => (row.original.reports === 0 ? "—" : count(row.original.messages)),
    meta: { align: "right" },
  },
  {
    id: "aligned",
    header: "Átment",
    cell: ({ row }) => shareText(row.original.aligned, row.original.messages),
    meta: { align: "right" },
  },
  {
    id: "unaligned",
    header: "Hitelesítés nélkül",
    cell: ({ row }) =>
      row.original.unaligned === 0 ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        <span className="flex flex-col" title="SPF és DKIM is bukott — nem tőlünk jött, vagy rosszul beállított küldő">
          <span>{count(row.original.unaligned)} levél</span>
          <span className="data-mono text-label text-muted-foreground">
            {row.original.unaligned_sources.map((source) => source.source_ip).join(", ")}
          </span>
        </span>
      ),
    meta: { wrap: true },
  },
];

function DmarcCard({
  rows,
  sources,
  error,
}: {
  rows: DmarcDomain[] | null;
  sources: WatchSource[] | null;
  error: string | null;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>DMARC-riportok</CardTitle>
        <CardDescription>
          A fogadók összesítő riportjai az utolsó 14 napról: mennyi levelet láttak a
          tartományainkról, és mennyi ment át (SPF vagy DKIM rendben).
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {error !== null && <ErrorState message={error} />}
        {rows === null ? (
          <LoadingState lines={2} />
        ) : rows.length === 0 ? (
          <NoData
            source="dmarc"
            sources={sources}
            what="A riportok egy rua-címre jönnek (EMC_DMARC_REPORT_DIR vagy EMC_DMARC_QUEUE_URL)."
          />
        ) : (
          <DataTable
            label="DMARC"
            variant="plain"
            className="-mx-5.5 border-t border-separator"
            data={rows}
            getRowId={(row) => row.domain}
            columns={DMARC_COLUMNS}
          />
        )}
      </CardContent>
    </Card>
  );
}

const SEED_COLUMNS: ColumnDef<SeedCheck>[] = [
  {
    id: "when",
    header: "Mikor",
    cell: ({ row }) => minute(row.original.occurred_at),
    meta: { mono: true, muted: true },
  },
  {
    id: "campaign",
    header: "Kampány",
    cell: ({ row }) => `#${row.original.campaign_id}`,
    meta: { mono: true },
  },
  { id: "tool", header: "Eszköz", cell: ({ row }) => row.original.tool },
  { id: "score", header: "Pont", cell: ({ row }) => row.original.score, meta: { align: "right" } },
  { id: "actor", header: "Ki", cell: ({ row }) => row.original.actor, meta: { muted: true } },
  { id: "notes", header: "Megjegyzés", cell: ({ row }) => row.original.notes, meta: { wrap: true } },
];

// --- the page -------------------------------------------------------------------------

export default function Deliverability() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [params, setParams] = useSearchParams();
  const pool = (params.get("pool") ?? "").toUpperCase();

  const [today, setToday] = useState(() => utcDay(new Date()));
  const [range, setRange] = useState<DayRange>(() => presetRange("7", utcDay(new Date())));
  const [openDay, setOpenDay] = useState<string | null>(null);
  const [asOf, setAsOf] = useState<Date | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const span = fetchWindow(range);
  const [pools, reloadPools] = useLoad(fetchPoolMeta, []);
  const [days, reloadDays] = useLoad(
    () => fetchDailyLive({ from: span.from, to: span.to, pool: pool || undefined }),
    [span.from, span.to, pool],
  );
  const [strip, reloadStrip] = useLoad(
    () =>
      fetchDailyLive({ from: addDays(today, -1), to: today }).then((rows) => {
        setAsOf(new Date());
        return rows;
      }),
    [today],
  );
  const [alerts, reloadAlerts] = useLoad(() => fetchAlerts(true), []);
  const [reputation, reloadReputation] = useLoad(
    () => fetchReputation({ from: addDays(range.to, -13), to: range.to }),
    [range.to],
  );
  const [dnsbl, reloadDnsbl] = useLoad(() => fetchDnsbl(2), []);
  const [dmarc, reloadDmarc] = useLoad(() => fetchDmarc(14), []);
  const [typo, reloadTypo] = useLoad(fetchTypoDomains, []);
  const [evidence, reloadEvidence] = useLoad(
    () => fetchEvidence({ from: range.from, to: range.to }),
    [range.from, range.to],
  );
  const [seeds] = useLoad(fetchSeedChecks, []);
  const [sources, reloadSources] = useLoad(fetchWatchSources, []);

  const refresh = useCallback(() => {
    const now = utcDay(new Date());
    if (now !== today) setToday(now);
    reloadPools();
    reloadDays();
    reloadStrip();
    reloadAlerts();
    reloadReputation();
    reloadDnsbl();
    reloadDmarc();
    reloadTypo();
    reloadEvidence();
    reloadSources();
  }, [
    today,
    reloadPools,
    reloadDays,
    reloadStrip,
    reloadAlerts,
    reloadReputation,
    reloadDnsbl,
    reloadDmarc,
    reloadTypo,
    reloadEvidence,
    reloadSources,
  ]);

  useEffect(() => {
    const timer = window.setInterval(refresh, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const poolOrder = useMemo(() => (pools.data ?? []).map((row) => row.pool), [pools.data]);
  const countriesOf = useCallback(
    (code: string) => {
      const names =
        (pools.data ?? []).find((row) => row.pool === code)?.countries.map((item) => item.country) ?? [];
      // "GR GR" says nothing; "KIS BG CZ EU IT SK" does.
      return names.length === 1 && names[0] === code ? "" : names.join(" ");
    },
    [pools.data],
  );
  const inView = useMemo(
    () => (pools.data ?? []).filter((row) => pool === "" || row.pool === pool).flatMap((row) => row.countries),
    [pools.data, pool],
  );

  const redItems = useMemo(
    () =>
      redNow({
        today,
        days: strip.data ?? [],
        pools: pools.data ?? [],
        dnsbl: dnsbl.data ?? [],
        reputation: reputation.data ?? [],
        alerts: alerts.data ?? [],
        sources: sources.data ?? [],
      }).filter((item) => pool === "" || item.pool === null || item.pool === pool),
    [today, strip.data, pools.data, dnsbl.data, reputation.data, alerts.data, sources.data, pool],
  );
  const sentOn = (day: string) =>
    (strip.data ?? [])
      .filter((row) => row.day === day && (pool === "" || row.pool === pool))
      .reduce((sum, row) => sum + row.totals.sent, 0);

  const table = useMemo(
    () => rangeTable(days.data ?? [], range, pool ? [pool] : poolOrder),
    [days.data, range, pool, poolOrder],
  );
  const perDay = useMemo(() => dayRows(days.data ?? [], range), [days.data, range]);

  async function onAcknowledge(row: AlertRow) {
    setBusy(true);
    setActionError(null);
    try {
      await acknowledgeAlert(row.id);
      reloadAlerts();
    } catch (cause: unknown) {
      setActionError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  async function onOpenDigest() {
    setBusy(true);
    setActionError(null);
    try {
      const html = await fetchDigestHtml(addDays(today, -1));
      const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
      window.open(url, "_blank", "noopener");
      // The tab has the blob by now; revoking later frees it without racing
      // the open.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (cause: unknown) {
      setActionError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  const alertColumns: ColumnDef<AlertRow>[] = [
    {
      id: "when",
      header: "Mikor",
      cell: ({ row }) => minute(row.original.occurred_at),
      meta: { mono: true, muted: true },
    },
    {
      id: "level",
      header: "Szint",
      cell: ({ row }) => <StatusPill domain="alert" state={row.original.level} />,
    },
    {
      id: "pool",
      header: "Pool",
      cell: ({ row }) => (row.original.pool ? <PoolLink pool={row.original.pool} /> : "—"),
    },
    { id: "message", header: "Üzenet", cell: ({ row }) => row.original.message, meta: { wrap: true } },
    { id: "kind", header: "Fajta", cell: ({ row }) => row.original.kind, meta: { muted: true, className: "font-mono text-detail" } },
    {
      id: "notified",
      header: "Kiküldve",
      cell: ({ row }) =>
        row.original.notified_at === null
          ? `nem (${row.original.notify_attempts} próba)`
          : minute(row.original.notified_at),
      meta: { muted: true },
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Műveletek</span>,
      cell: ({ row }) =>
        isAdmin && (
          <Button
            variant="outline"
            size="sm"
            type="button"
            disabled={busy}
            onClick={() => void onAcknowledge(row.original)}
          >
            Nyugtázás
          </Button>
        ),
      meta: { align: "right" },
    },
  ];

  const openAlerts = (alerts.data ?? []).filter((row) => pool === "" || row.pool === null || row.pool === pool);

  return (
    <>
      <PageHeader
        title="Kézbesítés"
        description={
          <>
            Poolonként és levelezőnként: mi ment ki, mi jött vissza, és hol áll a 4. szabály
            határaihoz képest. A mai nap élőben számolódik, a korábbiakat a reggeli
            újraszámolás adja; minden nap megmondja, mikori.
          </>
        }
        actions={
          <>
            <Button variant="outline" type="button" onClick={refresh}>
              <RefreshCwIcon />
              Frissítés
            </Button>
            <Button variant="outline" type="button" onClick={() => void onOpenDigest()} disabled={busy}>
              <FileTextIcon />
              Tegnapi digest
            </Button>
          </>
        }
      />

      <div className="flex flex-col gap-6">
        {actionError !== null && <ErrorState message={actionError} />}

        <RedStrip
          items={redItems}
          loading={strip.data === null && strip.error === null}
          asOf={asOf}
          todaySent={sentOn(today)}
          yesterdaySent={sentOn(addDays(today, -1))}
        />
        {strip.error !== null && <ErrorState title="A mai számok nem jöttek meg" message={strip.error} />}

        {openAlerts.length > 0 && (
          <Card id="riasztasok">
            <CardHeader>
              <CardTitle>Nyitott riasztások</CardTitle>
              <CardDescription>
                Ami kiment (vagy kimegy) Telegramra, Slackre, emailben — nyugtázásig itt marad.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                label="Nyitott riasztások"
                variant="plain"
                className="-mx-5.5 border-t border-separator"
                columns={alertColumns}
                data={openAlerts}
                getRowId={(row) => String(row.id)}
              />
            </CardContent>
          </Card>
        )}

        <Toolbar className="items-start justify-between gap-4">
          <DayRangePicker value={range} today={today} onChange={setRange} countries={inView} />
          <label className="flex items-center gap-2">
            <span className="caps-label">Pool</span>
            <NativeSelect
              aria-label="Pool"
              value={pool}
              onChange={(event) => {
                const next = new URLSearchParams(params);
                if (event.target.value) next.set("pool", event.target.value);
                else next.delete("pool");
                setParams(next, { replace: true });
              }}
            >
              <option value="">Minden pool</option>
              {poolOrder.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </NativeSelect>
          </label>
        </Toolbar>

        <Card>
          <CardHeader>
            <CardTitle>Poolok és levelezők</CardTitle>
            <CardDescription>
              Az időszak összesen; a szikra az utolsó 8 nap, a szaggatott vonal az emelési határ. A
              pool sora a 4. szabály mércéje, a levelezőké ugyanazokkal a határokkal olvasva;{" "}
              {POOL_MIN_SAMPLE} küldés alatt a szabály nem dönt, ezért ott a szám szürke.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {days.error !== null && <ErrorState message={days.error} onRetry={reloadDays} />}
            <DataTable
              label="Poolok és levelezők"
              variant="plain"
              className="-mx-5.5 border-t border-separator"
              columns={rangeColumns(countriesOf)}
              data={table}
              loading={days.data === null && days.error === null}
              getRowId={(row) => row.key}
              rowClassName={(row) => (row.isp === null ? "bg-surface-2 [&>td]:font-medium" : undefined)}
              empty={
                <EmptyState
                  bare
                  title="Ebben az időszakban nem ment ki levél."
                  description="Válassz hosszabb időszakot, vagy nézd meg a pool warmup-lapját."
                />
              }
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Napról napra</CardTitle>
            <CardDescription>
              Poolonként és naponta — ez az egység, amit a 4. szabály megítél. A sor kinyitva a
              levelezők bontását mutatja; az „eltérés” azt jelzi, hogy a beérkezéskor növelt
              számláló mást mond, mint az újraszámolás.
            </CardDescription>
            <CardAction>
              <span className="text-label text-muted-foreground">{perDay.length} sor</span>
            </CardAction>
          </CardHeader>
          <CardContent>
            <DataTable
              label="Napról napra"
              variant="plain"
              className="-mx-5.5 border-t border-separator"
              columns={DAY_COLUMNS}
              data={perDay}
              loading={days.data === null && days.error === null}
              getRowId={(row) => row.key}
              onRowClick={(row) => setOpenDay(openDay === row.key ? null : row.key)}
              isRowSelected={(row) => row.key === openDay}
              renderExpanded={(row) =>
                openDay === row.key ? (
                  <DataTable
                    label={`Levelezők, ${row.pool}, ${row.day}`}
                    variant="plain"
                    className="rounded-card border border-line bg-surface"
                    columns={ISP_DAY_COLUMNS}
                    data={row.source.by_isp.filter((item) => item.sent + item.delivered + item.bounced > 0)}
                    getRowId={(item) => item.isp}
                    empty={<p className="px-4 py-3 text-muted-foreground">Egyik levelezőnél sincs mozgás.</p>}
                  />
                ) : null
              }
              empty={<EmptyState bare title="Ebben az időszakban nincs nap forgalommal." />}
            />
          </CardContent>
        </Card>

        <EvidenceCard data={evidence.data} error={evidence.error} />

        <ReputationCard reputation={reputation.data} sources={sources.data} error={reputation.error} />

        <div className="grid gap-6 xl:grid-cols-2">
          <DnsblCard rows={dnsbl.data} sources={sources.data} error={dnsbl.error} />
          <DmarcCard rows={dmarc.data} sources={sources.data} error={dmarc.error} />
        </div>

        <TypoDomainsCard data={typo.data} error={typo.error} />

        <Card>
          <CardHeader>
            <CardTitle>Seed-tesztek</CardTitle>
            <CardDescription>
              A kampány előtti kézi inbox-teszt eredménye (docs/checklist-kampany-elott.md). Az
              indítás nem függ tőle, de a naplóban látszik, megvolt-e.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {seeds.error !== null ? (
              <ErrorState message={seeds.error} />
            ) : seeds.data === null ? (
              <LoadingState lines={2} />
            ) : seeds.data.length === 0 ? (
              <EmptyState bare title="Még egy kampányhoz sem rögzítettek seed-tesztet." />
            ) : (
              <DataTable
                label="Seed-tesztek"
                variant="plain"
                className="-mx-5.5 border-t border-separator"
                columns={SEED_COLUMNS}
                data={seeds.data}
                getRowId={(row) => `${row.campaign_id}/${row.occurred_at}`}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
